import {Permission} from './permission';

/** Which of a role's two data scopes governs an action: `readScope` or `writeScope`. */
export enum DataScopeAxis {
  READ = 'read',
  WRITE = 'write'
}

/** Permissions that act on a single trust account or something under it. */
export type TrustAccountAction = Exclude<
  Permission,
  | Permission.TRUST_ACCOUNTS_CREATE
  | Permission.TEAM_READ
  | Permission.TEAM_MANAGE_MEMBERS
  | Permission.TEAM_INVITE_MEMBERS
  | Permission.FIRM_SETTINGS_UPDATE
>;

/**
 * Which scope axis each trust-account action is checked against — the single source the API's
 * `authorize()` decides from. Lives here rather than in the API so client gating can share it.
 */
export const TRUST_ACCOUNT_ACTION_SCOPE_AXIS = {
  [Permission.TRUST_ACCOUNTS_READ]: DataScopeAxis.READ,
  [Permission.TRUST_ACCOUNTS_UPDATE]: DataScopeAxis.WRITE,
  [Permission.TRUST_ACCOUNTS_SIGN_DECLARATIONS]: DataScopeAxis.WRITE,
  [Permission.TRUST_ACCOUNTS_DELETE]: DataScopeAxis.WRITE,
  [Permission.TRUST_ACCOUNTS_SUBMIT]: DataScopeAxis.WRITE,
  [Permission.TRUST_ACCOUNTS_MANAGE_MEMBERS]: DataScopeAxis.WRITE,

  [Permission.BUYERS_READ]: DataScopeAxis.READ,
  [Permission.BUYERS_CREATE]: DataScopeAxis.WRITE,
  [Permission.BUYERS_UPDATE]: DataScopeAxis.WRITE,
  [Permission.BUYERS_DELETE]: DataScopeAxis.WRITE,

  [Permission.BENEFICIARIES_CREATE]: DataScopeAxis.WRITE,
  [Permission.BENEFICIARIES_UPDATE]: DataScopeAxis.WRITE,
  [Permission.BENEFICIARIES_DELETE]: DataScopeAxis.WRITE,

  [Permission.PAYMENTS_READ]: DataScopeAxis.READ,
  [Permission.PAYMENTS_COLLECT]: DataScopeAxis.WRITE,
  [Permission.PAYMENTS_RESEND]: DataScopeAxis.WRITE,
  [Permission.PAYMENTS_PAYOUT]: DataScopeAxis.WRITE,

  [Permission.DOCUMENTS_READ]: DataScopeAxis.READ,
  [Permission.DOCUMENTS_UPLOAD]: DataScopeAxis.WRITE,
  [Permission.DOCUMENTS_DELETE]: DataScopeAxis.WRITE,
  [Permission.DOCUMENTS_REVIEW_EXTRACTION]: DataScopeAxis.WRITE
} as const satisfies Record<TrustAccountAction, DataScopeAxis>;

type TrustAccountActionOnAxis<A extends DataScopeAxis> = {
  [K in TrustAccountAction]: (typeof TRUST_ACCOUNT_ACTION_SCOPE_AXIS)[K] extends A ? K : never;
}[TrustAccountAction];

export type TrustAccountReadAction = TrustAccountActionOnAxis<DataScopeAxis.READ>;
export type TrustAccountWriteAction = TrustAccountActionOnAxis<DataScopeAxis.WRITE>;

export const TRUST_ACCOUNT_ACTIONS = Object.keys(
  TRUST_ACCOUNT_ACTION_SCOPE_AXIS
) as readonly TrustAccountAction[];

export function isTrustAccountWriteAction(
  action: TrustAccountAction
): action is TrustAccountWriteAction {
  return TRUST_ACCOUNT_ACTION_SCOPE_AXIS[action] === DataScopeAxis.WRITE;
}

export const TRUST_ACCOUNT_READ_ACTIONS = TRUST_ACCOUNT_ACTIONS.filter(
  (action): action is TrustAccountReadAction => !isTrustAccountWriteAction(action)
);
